import { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { pythonVmInstance } from "../../utils/AxiosInstances/RareboxPythonInstance";

const REFRESH_MS = 30000;

const stateChip = (state) => {
	const map = {
		active: { color: "success", label: "ACTIVE" },
		retrying: { color: "warning", label: "RETRYING" },
		paused: { color: "error", label: "PAUSED" },
		not_installed: { color: "default", label: "NOT INSTALLED" },
	};
	const cfg = map[state] || { color: "default", label: (state || "UNKNOWN").toUpperCase() };
	return <Chip color={cfg.color} label={cfg.label} size="small" />;
};

const StatCard = ({ label, value }) => (
	<Paper variant="outlined" sx={{ p: 1.5, minWidth: 170 }}>
		<Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
			{label}
		</Typography>
		<Typography variant="subtitle1" fontWeight={600}>
			{value ?? "-"}
		</Typography>
	</Paper>
);

const PragmaAutoMail = () => {
	const [status, setStatus] = useState(null);
	const [events, setEvents] = useState([]);
	const [filter, setFilter] = useState("all");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [toggling, setToggling] = useState(false);

	const fetchAll = useCallback(async (statusFilter) => {
		try {
			const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
			const [st, ev] = await Promise.all([
				pythonVmInstance.get("/pragma-auto-mail/status"),
				pythonVmInstance.get("/pragma-auto-mail/events", { params: { limit: 100, ...params } }),
			]);
			setStatus(st.data.result);
			setEvents(ev.data.result.events || []);
			setError("");
		} catch (e) {
			setError(e.response?.data?.message || e.message);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchAll(filter);
		const timer = setInterval(() => fetchAll(filter), REFRESH_MS);
		return () => clearInterval(timer);
	}, [fetchAll, filter]);

	const handleToggle = async () => {
		setToggling(true);
		try {
			await pythonVmInstance.post("/pragma-auto-mail/toggle");
			await fetchAll(filter);
		} catch (e) {
			setError(e.response?.data?.message || e.message);
		} finally {
			setToggling(false);
			setConfirmOpen(false);
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	const paused = Boolean(status?.paused);

	return (
		<Box sx={{ p: 2 }}>
			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
				<Typography variant="h6">Pragma Auto Mail</Typography>
				{stateChip(status?.state)}
				<Box sx={{ flexGrow: 1 }} />
				<Button
					size="small"
					startIcon={<RefreshRoundedIcon />}
					onClick={() => fetchAll(filter)}>
					Refresh
				</Button>
				<Button
					size="small"
					variant="contained"
					color={paused ? "success" : "error"}
					startIcon={paused ? <PlayCircleRoundedIcon /> : <PauseCircleRoundedIcon />}
					disabled={status?.state === "not_installed"}
					onClick={() => setConfirmOpen(true)}>
					{paused ? "Start" : "Stop"}
				</Button>
			</Stack>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
					{error}
				</Alert>
			)}

			<Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
				<StatCard label="Last cycle" value={status?.last_cycle_at} />
				<StatCard label="Last email sent" value={status?.last_sent_at} />
				<StatCard label="Checkpoint (DB time)" value={status?.checkpoint} />
				<StatCard label="Total sent" value={status?.total_sent} />
				<StatCard label="Total failed" value={status?.total_failed} />
			</Stack>

			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
				<Typography variant="subtitle2">Send history</Typography>
				<ToggleButtonGroup
					size="small"
					exclusive
					value={filter}
					onChange={(e, v) => v && setFilter(v)}>
					<ToggleButton value="all">All</ToggleButton>
					<ToggleButton value="sent">Sent</ToggleButton>
					<ToggleButton value="failed">Failed</ToggleButton>
				</ToggleButtonGroup>
			</Stack>

			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Time</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Order</TableCell>
							<TableCell>Order No.</TableCell>
							<TableCell>Reason</TableCell>
							<TableCell>Template</TableCell>
							<TableCell>Customer</TableCell>
							<TableCell align="right">Items</TableCell>
							<TableCell align="right">Attempts</TableCell>
							<TableCell>Detail</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{events.length === 0 && (
							<TableRow>
								<TableCell colSpan={10} align="center">
									No events yet
								</TableCell>
							</TableRow>
						)}
						{events.map((e) => (
							<TableRow key={e.id} hover>
								<TableCell>{e.ts}</TableCell>
								<TableCell>
									<Chip
										size="small"
										color={e.status === "sent" ? "success" : "error"}
										label={e.status}
									/>
								</TableCell>
								<TableCell>{e.order_id}</TableCell>
								<TableCell>{e.order_number}</TableCell>
								<TableCell>{e.reason}</TableCell>
								<TableCell>{e.template}</TableCell>
								<TableCell>
									{e.cx_name}
									{e.cx_email ? ` (${e.cx_email})` : ""}
								</TableCell>
								<TableCell align="right">{e.item_count}</TableCell>
								<TableCell align="right">{e.attempts}</TableCell>
								<TableCell>{e.detail}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
				Auto-refreshes every 30s. Pausing stops emails but freezes the checkpoint — resuming
				catches up on everything cancelled while paused.
			</Typography>

			<Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
				<DialogTitle>{paused ? "Start email sending?" : "Stop email sending?"}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{paused
							? "The monitor will catch up on every cancellation that happened while paused and resume sending customer emails."
							: "No cancellation emails will be sent while paused. The checkpoint freezes, so resuming later catches up — nothing is lost."}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
					<Button
						variant="contained"
						color={paused ? "success" : "error"}
						disabled={toggling}
						onClick={handleToggle}>
						{paused ? "Start" : "Stop"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PragmaAutoMail;
